import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  CompareArrows as CompareArrowsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'sonner';
import { promptLabService } from '../../services/PromptLabService';
import { PromptVariablesPanel } from '../../components/PromptVariablesPanel';
import CodeMirror from '@uiw/react-codemirror';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { StreamLanguage } from '@codemirror/language';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';

// Custom syntax highlighting for variables in curly braces
const variableHighlight = HighlightStyle.define([
  { tag: t.variableName, color: '#4ade80', fontWeight: 'bold' },
  { tag: t.bracket, color: '#fbbf24' },
  { tag: t.string, color: '#60a5fa' },
]);

// Simple tokenizer for {variable} patterns
const promptTemplateLanguage = StreamLanguage.define({
  token: (stream: any) => {
    // Check for variable pattern {variable_name}
    if (stream.match(/\{[a-zA-Z_][a-zA-Z0-9_]*\}/)) {
      return 'variableName';
    }
    // Check for opening brace
    if (stream.match('{')) {
      return 'bracket';
    }
    // Check for closing brace
    if (stream.match('}')) {
      return 'bracket';
    }
    // Move forward one character if no match
    stream.next();
    return null;
  }
});

export function PromptLabEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [promptName, setPromptName] = useState('');
  const [usageCategory, setUsageCategory] = useState('');
  const [promptType, setPromptType] = useState('system_prompt');
  const [usageTypes, setUsageTypes] = useState<any[]>([]);
  const [loadingUsageTypes, setLoadingUsageTypes] = useState(true);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [promptVariables, setPromptVariables] = useState<any[]>([]);
  const [loadingPromptVariables, setLoadingPromptVariables] = useState(true);
  const [invalidVariables, setInvalidVariables] = useState<string[]>([]);
  
  // Version management state
  const [promptVersions, setPromptVersions] = useState<any[]>([]);
  const [currentVersion, setCurrentVersion] = useState<any>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Track original values for edit mode to detect changes
  const [originalPromptName, setOriginalPromptName] = useState('');
  const [originalUsageCategory, setOriginalUsageCategory] = useState('');
  const [originalSystemPrompt, setOriginalSystemPrompt] = useState('');
  const [originalPromptType, setOriginalPromptType] = useState('system_prompt');
  
  // Compare modal state
  const [showCompare, setShowCompare] = useState(false);

  const initialLoading = loadingUsageTypes || loadingPrompts;

  // Function to extract variables from prompt text
  const extractVariables = (text: string): string[] => {
    const regex = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
    const matches = text.matchAll(regex);
    return Array.from(matches, match => match[1]);
  };

  // Function to validate variables
  const validateVariables = (text: string) => {
    const usedVariables = extractVariables(text);
    const validVariableNames = promptVariables.map(v => v.name);
    const invalid = usedVariables.filter(v => !validVariableNames.includes(v));
    setInvalidVariables(invalid);
    return invalid.length === 0;
  };

  useEffect(() => {
    const fetchUsageTypes = async () => {
      try {
        const data = await promptLabService.getUsageTypes();
        setUsageTypes(data);
      } catch (error) {
        console.error('Failed to load usage types:', error);
      } finally {
        setLoadingUsageTypes(false);
      }
    };

    fetchUsageTypes();
  }, []);

  useEffect(() => {
    const fetchPromptVariables = async () => {
      try {
        const data = await promptLabService.getPromptVariables();
        setPromptVariables(data.prompt_variables || []);
      } catch (error) {
        console.error('Failed to load prompt variables:', error);
      } finally {
        setLoadingPromptVariables(false);
      }
    };

    fetchPromptVariables();
  }, []);

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const data = await promptLabService.getPrompts();
        setPrompts(data);
      } catch (error) {
        console.error('Failed to load prompts:', error);
      } finally {
        setLoadingPrompts(false);
      }
    };

    fetchPrompts();
  }, []);

  // Load prompt data when editing
  useEffect(() => {
    if (id && prompts.length > 0 && usageTypes.length > 0) {
      const prompt = prompts.find(p => p.id === id);
      if (prompt) {
        setCurrentVersion(prompt);
        
        // Set prompt type if available
        if (prompt.prompt_type) {
          setPromptType(prompt.prompt_type);
          setOriginalPromptType(prompt.prompt_type);
        }
        
        // Set versions from the prompt's versions array
        if (prompt.versions && Array.isArray(prompt.versions)) {
          // Sort by version_tag descending and take first 3
          const sortedVersions = [...prompt.versions].sort((a, b) => b.version_tag - a.version_tag); //.slice(0, 3);
          setPromptVersions(sortedVersions);
        }
        
        // Set the prompt name
        if (prompt.name) {
          setPromptName(prompt.name);
          setOriginalPromptName(prompt.name);
        }
        
        // Find and set the usage category
        const usageType = usageTypes.find(type => type.id === prompt.usage_type);
        if (usageType) {
          setUsageCategory(usageType.name);
          setOriginalUsageCategory(usageType.name);
        }
        
        // Get the system_prompt from the version that matches current_version
        if (prompt.versions && Array.isArray(prompt.versions) && prompt.current_version) {
          const currentVersionObj = prompt.versions.find((v: any) => v.version_tag === prompt.current_version);
          if (currentVersionObj && currentVersionObj.system_prompt) {
            const formattedPrompt = currentVersionObj.system_prompt.split('\\n').join('\n');
            setSystemPrompt(formattedPrompt);
            setOriginalSystemPrompt(formattedPrompt);
            // Set the latest version as selected initially
            setSelectedVersionId(currentVersionObj.id);
          }
        }
      }
    }
  }, [id, prompts, usageTypes]);

  // Validate variables when system prompt or available variables change
  useEffect(() => {
    if (systemPrompt && promptVariables.length > 0) {
      validateVariables(systemPrompt);
    }
  }, [systemPrompt, promptVariables]);

  // Check if there are any changes in edit mode
  const hasChanges = () => {
    return (
      promptName.trim() !== originalPromptName ||
      usageCategory !== originalUsageCategory ||
      systemPrompt !== originalSystemPrompt ||
      promptType !== originalPromptType
    );
  };

  const handleSaveTemplate = async () => {
    if (!promptName.trim()) {
      toast.error('Name is required.');
      return;
    }

    if (!systemPrompt.trim()) {
      toast.error('System prompt is required.');
      return;
    }

    if (!usageCategory) {
      toast.error('Usage category is required.');
      return;
    }

    // Validate variables before saving
    if (invalidVariables.length > 0) {
      toast.error(`Invalid variables used: ${invalidVariables.map(v => `{${v}}`).join(', ')}. Please use only available variables.`);
      return;
    }

    setSaving(true);
    try {
      const selectedType = usageTypes.find(type => type.name === usageCategory);
      if (!selectedType) {
        toast.error('Selected usage type not found.');
        return;
      }

      const formattedPrompt = systemPrompt.split('\n').join('\\n');

      const payload = {
        name: promptName.trim(),
        usage_type_id: selectedType.id,
        system_prompt: formattedPrompt,
        prompt_type: promptType,
        is_active: true
      };

      await promptLabService.updatePrompt(id!, payload);
      toast.success('Prompt updated successfully!');

      // Refresh the prompts list
      const updatedPrompts = await promptLabService.getPrompts();
      setPrompts(updatedPrompts);
      
      navigate(ROUTE_PATHS.PROMPT_LAB);
    } catch (error: any) {
      console.error('Failed to save template:', error);
      toast.error(`Failed to save template: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    navigate(ROUTE_PATHS.PROMPT_LAB);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {initialLoading ? (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '60vh',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ color: '#64748b' }}>
              Loading Template...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 1
                  }}
                >
                  Prompt Template
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#64748b'
                  }}
                >
                  Test and refine your prompts with different parameters
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CompareArrowsIcon />}
                  onClick={() => setShowCompare(true)}
                  disabled={!hasChanges()}
                >
                  Compare Prompts
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                  onClick={handleSaveTemplate}
                  disabled={saving || !promptName.trim() || !systemPrompt.trim() || !usageCategory || !hasChanges() || invalidVariables.length > 0}
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </Button>
              </Box>
            </Box>

            {/* Editor View */}
            <Grid container spacing={3}>
              {/* Left Panel - System Prompt Editor */}
              <Grid size={{ xs: 12, lg: 8 }}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  {/* Name Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 1
                      }}
                    >
                      Name
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter prompt name"
                      value={promptName}
                      disabled
                      onChange={(e) => setPromptName(e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  {/* Usage Category Selection */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 1
                      }}
                    >
                      Usage Category
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        labelId="usage-category-label"
                        id="usage-category-select"
                        value={usageCategory}
                        onChange={(e) => setUsageCategory(e.target.value)}
                        disabled
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em style={{ color: '#94a3b8', fontStyle: 'normal' }}>Select usage category</em>;
                          }
                          const type = usageTypes.find(t => t.name === selected);
                          return type?.name || selected;
                        }}
                      >
                        {loadingUsageTypes ? (
                          <MenuItem key="loading-types" value="" disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
                          </MenuItem>
                        ) : usageTypes.length === 0 ? (
                          <MenuItem key="no-types" value="" disabled>
                            No usage types available
                          </MenuItem>
                        ) : (
                          usageTypes.map((type) => (
                            <MenuItem key={type.id} value={type.name}>
                              {type.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Prompt Type Selection */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#0f172a',
                        mb: 1
                      }}
                    >
                      Prompt Type
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={promptType}
                        onChange={(e) => {
                          console.log('Radio button changed to:', e.target.value);
                          setPromptType(e.target.value);
                        }}
                      >
                        <FormControlLabel
                          value="system_prompt"
                          control={<Radio />}
                          label="System Prompt"
                        />
                        <FormControlLabel
                          value="user_prompt"
                          control={<Radio />}
                          label="User Prompt"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#0f172a'
                        }}
                      >
                        {promptType === 'system_prompt' ? 'System Prompt' : 'User Prompt'}
                      </Typography>
                      {invalidVariables.length > 0 && (
                        <Chip
                          label={`${invalidVariables.length} invalid variable${invalidVariables.length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            bgcolor: '#fee2e2',
                            color: '#991b1b',
                            fontWeight: 600,
                            height: 22
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  {invalidVariables.length > 0 && (
                    <Box 
                      sx={{ 
                        bgcolor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 1,
                        p: 1.5,
                        mb: 2
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#991b1b',
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.5
                        }}
                      >
                        ⚠️ Invalid Variables Detected
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#b91c1c',
                          display: 'block'
                        }}
                      >
                        The following variables are not available: {invalidVariables.map(v => `{${v}}`).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* CodeMirror Editor */}
                  <Box
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: '#1e293b'
                    }}
                  >
                    {/* Editor Header */}
                    <Box
                      sx={{
                        bgcolor: '#0f172a',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #334155',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#10b981' }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', ml: 2 }}>
                        {promptType === 'system_prompt' ? 'system-prompt.txt' : 'user-prompt.txt'}
                      </Typography>
                    </Box>
                    
                    {/* CodeMirror Editor Content */}
                    <CodeMirror
                      value={systemPrompt}
                      height="500px"
                      theme={oneDark}
                      extensions={[
                        promptTemplateLanguage,
                        syntaxHighlighting(variableHighlight),
                        EditorView.baseTheme({
                          '.cm-variableName': {
                            color: '#4ade80 !important',
                            fontWeight: 'bold'
                          }
                        })
                      ]}
                      onChange={(value) => {
                        setSystemPrompt(value);
                      }}
                      placeholder="Select a usage category to load the prompt template...\n\nYou can use variables like {variable_name} in your prompt."
                      basicSetup={{
                        lineNumbers: true,
                        highlightActiveLineGutter: true,
                        highlightSpecialChars: true,
                        foldGutter: true,
                        drawSelection: true,
                        dropCursor: true,
                        allowMultipleSelections: true,
                        indentOnInput: true,
                        syntaxHighlighting: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: true,
                        rectangularSelection: true,
                        crosshairCursor: true,
                        highlightActiveLine: true,
                        highlightSelectionMatches: true,
                        closeBracketsKeymap: true,
                        searchKeymap: true,
                        foldKeymap: true,
                        completionKeymap: true,
                        lintKeymap: true,
                      }}
                      style={{
                        fontSize: '14px',
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Right Panel - Version History */}
              <Grid size={{ xs: 12, lg: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Prompt Variables Section */}
                  {promptVariables.length > 0 && (
                    <PromptVariablesPanel 
                      promptVariables={promptVariables}
                      maxHeight="600px"
                      useSticky={false}
                    />
                  )}
                  
                  {/* Version History */}
                  {promptVersions.length > 0 && (
                    <Paper sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#0f172a',
                          mb: 3
                        }}
                      >
                        Version History
                      </Typography>

                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 2,
                        ...(promptVersions.length > 3 && {
                          maxHeight: '420px',
                          overflowY: 'auto',
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f5f9',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#cbd5e1',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: '#94a3b8',
                            },
                          },
                        }),
                      }}>
                        {promptVersions.map((version: any) => {
                          const isLatest = currentVersion && version.version_tag === currentVersion.current_version;
                          const isSelected = selectedVersionId === version.id;
                          
                          return (
                            <Card
                              key={version.id}
                              onClick={() => {
                                // Load this version's system_prompt when clicked
                                if (version.system_prompt) {
                                  const formattedPrompt = version.system_prompt.split('\\n').join('\n');
                                  setSystemPrompt(formattedPrompt);
                                  setSelectedVersionId(version.id);
                                }
                              }}
                              sx={{
                                border: isSelected ? '2px solid #667eea' : '1px solid #e2e8f0',
                                borderRadius: 2,
                                bgcolor: isSelected ? '#f8fafc' : 'transparent',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                flexShrink: 0,
                                minHeight: 'fit-content',
                                '&:hover': {
                                  bgcolor: '#f8fafc',
                                  borderColor: '#667eea',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)'
                                }
                              }}
                            >
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 600,
                                        color: '#0f172a',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}
                                    >
                                      Version {version.version_tag || 'N/A'}
                                      {isLatest && (
                                        <Chip
                                          label="Latest"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            bgcolor: '#667eea',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            fontWeight: 600
                                          }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: '#64748b', display: 'block', mt: 0.5 }}
                                    >
                                      Created: {new Date(version.created_at).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                          display: 'block',
                          mt: 2,
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}
                      >
                        Click on any version to load its content in the editor
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )}

        {/* Compare Dialog */}
        <Dialog
          open={showCompare}
          onClose={() => setShowCompare(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              height: '90vh',
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid #e2e8f0',
            pb: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a' }}>
                Compare Changes
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Original version (left) vs Your changes (right)
              </Typography>
            </Box>
            <IconButton onClick={() => setShowCompare(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 0, overflow: 'auto', bgcolor: '#f8fafc' }}>
            {/* Custom Column Headers */}
            <Box sx={{ 
              display: 'flex',
              bgcolor: '#fafbfc',
              borderBottom: '2px solid #e1e4e8',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <Box sx={{ 
                flex: 1, 
                p: 2, 
                borderRight: '1px solid #e1e4e8',
                fontWeight: 600,
                fontSize: '14px',
                color: '#0f172a'
              }}>
                Original Version
              </Box>
              <Box sx={{ 
                flex: 1, 
                p: 2,
                fontWeight: 600,
                fontSize: '14px',
                color: '#0f172a'
              }}>
                Your Changes
              </Box>
            </Box>
            
            {/* Diff Viewer */}
            <Box sx={{ 
              '& pre': {
                fontFamily: '"Fira Code", "Courier New", monospace',
                fontSize: '14px'
              }
            }}>
              <ReactDiffViewer
                oldValue={originalSystemPrompt}
                newValue={systemPrompt}
                splitView={true}
                showDiffOnly={false}
                useDarkTheme={false}
                hideLineNumbers={false}
                styles={{
                  variables: {
                    light: {
                      diffViewerBackground: '#fff',
                      diffViewerColor: '#24292e',
                      addedBackground: '#e6ffed',
                      addedColor: '#24292e',
                      removedBackground: '#ffeef0',
                      removedColor: '#24292e',
                      wordAddedBackground: '#acf2bd',
                      wordRemovedBackground: '#fdb8c0',
                      addedGutterBackground: '#cdffd8',
                      removedGutterBackground: '#ffdce0',
                      gutterBackground: '#f6f8fa',
                      gutterBackgroundDark: '#f3f4f6',
                      highlightBackground: '#fffbdd',
                      highlightGutterBackground: '#fff5b1',
                    },
                  },
                  line: {
                    padding: '10px 2px',
                    '&:hover': {
                      background: '#f6f8fa',
                    },
                  },
                  gutter: {
                    padding: '10px 8px',
                    minWidth: '50px',
                  },
                  contentText: {
                    fontFamily: '"Fira Code", "Courier New", monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                  },
                }}
              />
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            borderTop: '1px solid #e2e8f0',
            px: 3,
            py: 2
          }}>
            <Button onClick={() => setShowCompare(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
